import React, { useState } from "react";
import { useLanguage } from "../i18n/LanguageContext";
import { createPortal } from "react-dom";
import { Link } from "../components/L";
import axios from "axios";
import { X } from "lucide-react";
import { speakerBios } from "./ModulePage";
import useSEO from "../hooks/useSEO";
import { translateDate } from "../i18n/dateTranslate";
import ScrollReveal from "../components/ScrollReveal";
import PhoneField from "../components/PhoneField";
import TestimonialsSection from "../components/TestimonialsSection";
import FloatingRegisterButton from "../components/FloatingRegisterButton";
import { masterclassModules as modules } from "../data/masterclassModules";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COVER_IMG = "/api/files/local/masterclass-poster-alt.png";
const MASTERCLASS_EVENT_ID = "masterclass-2026";

const getSpeakerImage = (name) => speakerBios[name]?.image || null;

/* Registration Popup */
const RegistrationPopup = ({ onClose, source = "" }) => {
  const { tx } = useLanguage();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", city: "", profession: "" });
  const [agreed, setAgreed] = useState(false);
  const [badgeFile, setBadgeFile] = useState(null);
  const [badgePreview, setBadgePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setBadgeFile(file);
      setBadgePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.first_name || !form.last_name || !form.email || !form.phone) {
      setError(tx("Ju lutem plotesoni fushat e detyrueshme."));
      return;
    }
    if (!badgeFile) { setError(tx("Ju lutem ngarkoni nje foto per badge.")); return; }
    if (!agreed) { setError(tx("Duhet te pranoni kushtet e perdorimit.")); return; }
    setSubmitting(true);
    try {
      let badgeUrl = "";
      if (badgeFile) {
        const fd = new FormData();
        fd.append("file", badgeFile);
        const uploadRes = await axios.post(`${API}/upload?private=true`, fd);
        badgeUrl = uploadRes.data.url || "";
      }
      await axios.post(`${API}/registrations`, {
        event_id: MASTERCLASS_EVENT_ID,
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        phone: form.phone,
        city: form.city,
        profession: form.profession,
        badge_photo: badgeUrl,
        message: source || "Regjistrim nga faqja Masterclass",
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || tx("Ndodhi nje gabim. Provoni perseri."));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" data-testid="registration-popup" style={{ top: 0, left: 0, right: 0, bottom: 0, position: 'fixed' }}>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto z-[201]" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-slate-700 z-10" data-testid="close-popup"><X size={20} /></button>

        {success ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-[#1CA3B0]/10 flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-check-circle text-3xl text-[#1CA3B0]"></i>
            </div>
            <h3 className="text-xl font-bold text-[#0A0D10] mb-2">{tx("Regjistrimi u krye me sukses!")}</h3>
            <p className="text-[#4A5568] mb-2">{tx("Faleminderit per regjistrimin. Do t'ju kontaktojme se shpejti per detajet.")}</p>
            <p className="text-sm text-[#828D9E] mb-6"><i className="fas fa-envelope mr-1.5 text-[#1CA3B0]"></i>{tx("Kontrollo email-in")} <span className="text-xs text-[#a0aec0]">{tx("(junk ose spam folder)")}</span> {tx("tuaj per detaje te metejshme.")}</p>
            <button onClick={onClose} className="rounded-lg px-6 py-2.5 font-bold text-white" style={{ background: 'linear-gradient(130deg, #3CD4E1 0%, #1CA3B0 100%)' }}>{tx("Mbyll")}</button>
          </div>
        ) : (
          <>
            <div className="p-6 pb-0">
              <div className="rounded-xl p-4 mb-4" style={{ background: 'linear-gradient(130deg, #003F72 0%, #0a0f1a 100%)' }}>
                <p className="text-[#3CD4E1] text-xs font-bold uppercase tracking-wider">{tx("Regjistrohu per")}</p>
                <h3 className="text-white font-bold text-lg">Modern Implantology Masterclass</h3>
                <p className="text-white/50 text-xs mt-1">{tx("6 Module | Qershor - Tetor 2026")}</p>
              </div>
            </div>
            <form onSubmit={handleSubmit} className="p-6 pt-2 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-[#4A5568] mb-1 block">{tx("Emri *")}</label>
                  <input type="text" value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-[#1CA3B0] focus:ring-1 focus:ring-[#1CA3B0]" placeholder={tx("Emri")} data-testid="reg-first-name" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#4A5568] mb-1 block">{tx("Mbiemri *")}</label>
                  <input type="text" value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-[#1CA3B0] focus:ring-1 focus:ring-[#1CA3B0]" placeholder={tx("Mbiemri")} data-testid="reg-last-name" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-[#4A5568] mb-1 block">Email *</label>
                  <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-[#1CA3B0] focus:ring-1 focus:ring-[#1CA3B0]" placeholder="email@example.com" data-testid="reg-email" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#4A5568] mb-1 block">{tx("Nr. Celulari *")}</label>
                  <PhoneField value={form.phone} onChange={(v) => setForm({ ...form, phone: v.startsWith("+") ? v : `+${v}` })} placeholder="6XX XXX XXX" dataTestId="reg-phone" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-[#4A5568] mb-1 block">{tx("Qyteti")}</label>
                  <input type="text" value={form.city} onChange={e => setForm({...form, city: e.target.value})} className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-[#1CA3B0] focus:ring-1 focus:ring-[#1CA3B0]" placeholder={tx("Tirane")} data-testid="reg-city" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#4A5568] mb-1 block">{tx("Profesioni")}</label>
                  <input type="text" value={form.profession} onChange={e => setForm({...form, profession: e.target.value})} className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-[#1CA3B0] focus:ring-1 focus:ring-[#1CA3B0]" placeholder={tx("Dentist, Ortodont, Laborant...")} data-testid="reg-profession" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-[#4A5568] mb-1 block">{tx("Ngarko Foto (per Badge) *")}</label>
                <div className="flex items-center gap-3">
                  <label className="flex-1 cursor-pointer">
                    <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-dashed border-slate-300 hover:border-[#1CA3B0] transition-colors text-sm text-[#828D9E]">
                      <i className="fas fa-camera text-[#1CA3B0]"></i>
                      {badgeFile ? badgeFile.name : tx("Zgjidh foto...")}
                    </div>
                    <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" data-testid="reg-badge-photo" />
                  </label>
                  {badgePreview && <img src={badgePreview} alt="Preview" className="w-12 h-12 rounded-lg object-cover border border-slate-200" />}
                </div>
              </div>
              <div className="flex items-start gap-2 pt-1">
                <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} className="mt-1 accent-[#1CA3B0]" data-testid="reg-terms" />
                <label className="text-xs text-[#828D9E]">
                  {tx("Duke vazhduar, une pranoj")} <a href="/kontakt" className="text-[#1CA3B0] underline">{tx("Kushtet e Perdorimit")}</a> {tx("dhe")} <a href="/kontakt" className="text-[#1CA3B0] underline">{tx("Politika e Privatesise")}</a>.
                </label>
              </div>
              {error && <p className="text-red-500 text-xs">{error}</p>}
              <button type="submit" disabled={submitting} className="w-full rounded-lg py-3 font-bold text-white flex items-center justify-center gap-2 disabled:opacity-50" style={{ background: 'linear-gradient(130deg, #3CD4E1 0%, #1CA3B0 100%)' }} data-testid="reg-submit">
                {submitting ? tx("Duke regjistruar...") : tx("Regjistrohu Tani")} {!submitting && <i className="fas fa-arrow-right text-sm"></i>}
              </button>
              <div className="text-center pt-1">
                <a href="https://wa.me/355696056794?text=Pershendetje%2C%20dua%20te%20di%20me%20shume%20per%20Masterclass-in" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-semibold text-green-600 hover:text-green-700" data-testid="reg-whatsapp">
                  <i className="fab fa-whatsapp text-lg"></i> {tx("Per me shume informacion, na kontaktoni ne WhatsApp")}
                </a>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

const MasterclassPage = () => {
  const { tx, lang } = useLanguage();
  const [showRegPopup, setShowRegPopup] = useState(false);

  useSEO({
    title: "Modern Implantology Masterclass - 6 Module Intensive",
    description: tx("Modern Implantology Masterclass - From Fundamentals to Full-Arch Excellence. 6 module intensive me 9+ eksperte nderkombetare, nga Qershori deri ne Tetor 2026 ne Tirane."),
    keywords: "masterclass implantologji, kurse implanteve dentare, trajnim kirurgji orale, full-arch rehabilitation, GBR, sinus lift, implantologji moderne",
    canonical: "https://aligners.academy/evente/masterclass",
  });

  return (
    <div data-testid="masterclass-page">
      {showRegPopup && createPortal(<RegistrationPopup onClose={() => setShowRegPopup(false)} />, document.body)}
      <FloatingRegisterButton onClick={() => setShowRegPopup(true)} testId="masterclass-floating-register" />

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: '#0a0f1a', minHeight: '600px' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-96 h-96 rounded-full" style={{ background: 'radial-gradient(circle, #3CD4E1 0%, transparent 70%)' }} />
          <div className="absolute bottom-10 left-10 w-64 h-64 rounded-full" style={{ background: 'radial-gradient(circle, #003F72 0%, transparent 70%)' }} />
        </div>
        <div className="max-w-[1350px] mx-auto grid grid-cols-1 lg:grid-cols-2 items-center min-h-[600px] relative z-10">
          <div className="py-16 px-6 md:px-12">
            <div className="flex items-center gap-2 text-white/50 text-sm mb-8">
              <Link to="/" className="hover:text-white transition-colors">{tx("Kryefaqe")}</Link><span>/</span>
              <Link to="/evente" className="hover:text-white transition-colors">{tx("Evente")}</Link><span>/</span>
              <span className="text-[#3CD4E1]">Masterclass</span>
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#3CD4E1]/30 mb-6">
              <span className="w-2 h-2 rounded-full bg-[#3CD4E1] animate-pulse"></span>
              <span className="text-[#3CD4E1] text-xs font-bold uppercase tracking-widest">{tx("Regjistrime te Hapura")}</span>
            </div>
            <h1 className="text-white text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight" style={{ fontFamily: "'Noto Sans', sans-serif" }}>
              Modern Implantology<br/>Masterclass
            </h1>
            <p className="text-xl md:text-2xl italic mb-8" style={{ color: '#3CD4E1' }}>From Fundamentals to Full-Arch Excellence</p>
            <div className="grid grid-cols-3 gap-4 mb-10 max-w-md">
              {[{ n: "6", l: tx("Module") }, { n: "12", l: tx("Dite Trajnimi") }, { n: "9+", l: tx("Ekspert") }].map((s, i) => (
                <div key={i} className="text-center p-3 rounded-xl bg-white/5 border border-white/10">
                  <p className="text-2xl font-black text-white">{s.n}</p>
                  <p className="text-[10px] uppercase tracking-wider text-white/50 mt-1">{s.l}</p>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-4 text-sm text-white/60 mb-10">
              <span className="flex items-center gap-2"><i className="fas fa-calendar-alt text-[#3CD4E1]"></i>{tx("Qershor - Tetor 2026")}</span>
              <span className="flex items-center gap-2"><i className="fas fa-map-marker-alt text-[#3CD4E1]"></i>{tx("Tirane, Shqiperi")}</span>
              <span className="flex items-center gap-2"><i className="fas fa-graduation-cap text-[#3CD4E1]"></i>{tx("Certifikate")}</span>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button onClick={() => setShowRegPopup(true)} className="rounded-lg px-6 py-2.5 text-sm font-bold inline-flex items-center gap-2 text-white transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#3CD4E1]/25" style={{ background: 'linear-gradient(130deg, #3CD4E1 0%, #1CA3B0 100%)' }} data-testid="masterclass-register-btn">
                {tx("Regjistrohu Tani")} <i className="fas fa-arrow-right text-xs"></i>
              </button>
              <a href="/api/download/programi-masterclass" target="_blank" rel="noopener noreferrer" className="rounded-lg px-6 py-2.5 text-sm font-bold inline-flex items-center gap-2 text-white border border-white/20 hover:border-white/40 hover:bg-white/10 transition-all">
                <i className="fas fa-file-pdf"></i> {tx("Shiko Programin")}
              </a>
              <a href="#modules" className="rounded-lg px-6 py-2.5 text-sm font-bold inline-flex items-center gap-2 text-white/80 border border-white/20 hover:border-white/40 hover:text-white transition-all">
                {tx("Shiko Modulet")} <i className="fas fa-chevron-down text-xs"></i>
              </a>
            </div>
          </div>
          <div className="hidden lg:flex items-center justify-center p-8">
            <div className="relative">
              <div className="absolute -inset-4 rounded-2xl bg-gradient-to-br from-[#3CD4E1]/20 to-transparent blur-xl"></div>
              <img src={COVER_IMG} alt="Modern Implantology Masterclass" className="relative w-full max-w-sm rounded-2xl shadow-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="py-16 px-6 md:px-12 bg-[#f8fafb]">
        <div className="max-w-[1350px] mx-auto">
          <ScrollReveal direction="up">
            <p className="text-sm uppercase tracking-[0.15em] text-[#1CA3B0] font-bold mb-3 text-center">{tx("Pse ky Masterclass?")}</p>
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-12 text-[#0A0D10]" style={{ fontFamily: "'Noto Sans', sans-serif" }}>
              {tx("Programi me i plote i Implantologjise ne rajon")}
            </h2>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: "fa-microscope", title: tx("Hands-On Intensiv"), desc: tx("Praktike ne modele dhe live surgery ne paciente reale me mentorimin e eksperteve.") },
              { icon: "fa-globe-europe", title: tx("Ekspert Nderkombetar"), desc: tx("9+ lektore nga Italia, Tunizia, Maqedonia, Shqiperia dhe me gjere.") },
              { icon: "fa-certificate", title: tx("Certifikate e Njohur"), desc: tx("Certifikate profesionale ne perfundim te secilit modul dhe programit te plote.") },
              { icon: "fa-users", title: tx("Networking Ekskluziv"), desc: tx("Lidhu me koleget dhe nderto partneritete qe zgjasin me tej masterclass-it.") },
            ].map((item, i) => (
              <ScrollReveal key={i} direction="left" delay={i * 0.12}>
                <div className="p-6 rounded-2xl bg-white border border-slate-100 hover:shadow-lg hover:border-[#1CA3B0]/30 transition-all duration-300 text-center h-full">
                  <div className="w-14 h-14 rounded-xl mx-auto mb-4 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #3CD4E1 0%, #003F72 100%)' }}>
                    <i className={`fas ${item.icon} text-white text-xl`}></i>
                  </div>
                  <h3 className="font-bold text-[#0A0D10] mb-2">{item.title}</h3>
                  <p className="text-sm text-[#828D9E] leading-relaxed">{item.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Modules Timeline */}
      <section id="modules" className="py-20 px-6 md:px-12">
        <div className="max-w-[1350px] mx-auto">
          <ScrollReveal direction="up">
            <p className="text-sm uppercase tracking-[0.15em] text-[#1CA3B0] font-bold mb-3 text-center">{tx("Programi i Plote")}</p>
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-4 text-[#0A0D10]" style={{ fontFamily: "'Noto Sans', sans-serif" }}>
              {tx("6 Module, 12 Dite, 1 Mision")}
            </h2>
            <p className="text-center text-[#828D9E] mb-14 max-w-2xl mx-auto">
              {tx("Nga bazat e implantologjise deri te rehabilitimi full-arch. Cdo modul nderton mbi te meparshmin per te krijuar nje ekspert te plote.")}
            </p>
          </ScrollReveal>
          <div className="space-y-6">
            {modules.map((mod, idx) => (
              <ScrollReveal key={mod.id} direction={idx % 2 === 0 ? "right" : "left"} delay={0.05}>
                <Link to={`/evente/masterclass/${mod.id}`} className="block group" data-testid={`module-btn-${mod.id}`}>
                <div className="flex flex-col lg:flex-row items-stretch rounded-2xl border border-slate-200 bg-white overflow-hidden hover:border-[#1CA3B0]/40 hover:shadow-xl transition-all duration-500">
                  <div className="lg:w-28 flex-shrink-0 flex items-center justify-center py-4 lg:py-0" style={{ background: idx % 2 === 0 ? 'linear-gradient(180deg, #003F72 0%, #0a1628 100%)' : 'linear-gradient(180deg, #1CA3B0 0%, #003F72 100%)' }}>
                    <div className="text-center">
                      <p className="text-3xl font-black text-white">{mod.id}</p>
                      <p className="text-[10px] uppercase tracking-widest text-white/50">{tx("Modul")}</p>
                    </div>
                  </div>
                  <div className="flex-1 p-6 lg:p-8">
                    <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-8">
                      <div className="flex-1">
                        <span className="text-xs font-bold text-[#1CA3B0] bg-[#1CA3B0]/10 px-3 py-1 rounded-full">{translateDate(mod.date, lang)}</span>
                        <h3 className="text-lg font-bold text-[#0A0D10] mb-2 mt-2 group-hover:text-[#1CA3B0] transition-colors" style={{ fontFamily: "'Noto Sans', sans-serif" }}>{tx(mod.title)}</h3>
                        <p className="text-sm text-[#828D9E] leading-relaxed">{tx(mod.desc)}</p>
                      </div>
                      <div className="flex items-center gap-3 lg:gap-4 flex-shrink-0">
                        <div className="flex -space-x-3">
                          {mod.speakers.map((s, si) => {
                            const img = getSpeakerImage(s);
                            return img ? (
                              <img key={si} src={img} alt={s} className="w-12 h-12 rounded-full object-cover border-2 border-white" style={{ background: '#0a0f1a' }} />
                            ) : (
                              <div key={si} className="w-12 h-12 rounded-full bg-slate-200 border-2 border-white flex items-center justify-center"><i className="fas fa-user text-slate-400 text-sm"></i></div>
                            );
                          })}
                        </div>
                        <div className="hidden sm:block">
                          {mod.speakers.map((s, si) => (<p key={si} className="text-xs text-[#4A5568] leading-tight">{s}</p>))}
                        </div>
                      </div>
                      <div className="hidden lg:flex items-center">
                        <div className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center group-hover:border-[#1CA3B0] group-hover:bg-[#1CA3B0] transition-all duration-300">
                          <i className="fas fa-arrow-right text-sm text-slate-400 group-hover:text-white transition-colors"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Speakers Grid */}
      <section className="relative py-20 px-6 md:px-12 overflow-hidden" style={{ background: '#0a0f1a' }}>
        <style>{`
          @keyframes floatOrb1 { 0%,100% { transform: translate(0,0); } 50% { transform: translate(30px,-20px); } }
          @keyframes floatOrb2 { 0%,100% { transform: translate(0,0); } 50% { transform: translate(-25px,15px); } }
          @keyframes floatOrb3 { 0%,100% { transform: translate(0,0); } 50% { transform: translate(15px,25px); } }
        `}</style>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 left-[10%] w-72 h-72 rounded-full opacity-[0.07]" style={{ background: 'radial-gradient(circle, #3CD4E1, transparent 70%)', animation: 'floatOrb1 8s ease-in-out infinite' }} />
          <div className="absolute bottom-20 right-[15%] w-56 h-56 rounded-full opacity-[0.05]" style={{ background: 'radial-gradient(circle, #1CA3B0, transparent 70%)', animation: 'floatOrb2 10s ease-in-out infinite' }} />
          <div className="absolute top-1/2 left-1/2 w-96 h-96 rounded-full opacity-[0.04]" style={{ background: 'radial-gradient(circle, #003F72, transparent 60%)', animation: 'floatOrb3 12s ease-in-out infinite' }} />
        </div>
        <div className="max-w-[1350px] mx-auto relative z-10">
          <ScrollReveal direction="up">
            <p className="text-sm uppercase tracking-[0.15em] text-[#3CD4E1] font-bold mb-3 text-center">{tx("Lektoret")}</p>
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-4 text-white" style={{ fontFamily: "'Noto Sans', sans-serif" }}>{tx("Eksperte qe Frymezojne")}</h2>
            <p className="text-center text-white/50 mb-14 max-w-2xl mx-auto">{tx("Mesoni nga lektoret me te mire kombetar dhe nderkombetar.")}</p>
          </ScrollReveal>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {Object.entries(speakerBios).filter(([_, s]) => s.image).map(([name, speaker], i) => (
              <ScrollReveal key={i} direction="up" delay={i * 0.08}>
                <div className="text-center group">
                  <div className="relative mb-4 rounded-xl overflow-hidden aspect-square">
                    <img src={speaker.image} alt={name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" style={{ background: '#0a0f1a' }} />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  </div>
                  <h4 className="text-white font-bold text-sm">{name}</h4>
                  <p className="text-[#3CD4E1] text-xs mt-0.5">{tx(speaker.title)}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials from past events */}
      <TestimonialsSection
        variant="dark"
        eyebrow={tx("Testimoniale")}
        heading={tx("Deshmi nga eventet e kaluara")}
        description={tx("Pjesemarresit tane ndajne pervojen e tyre nga workshop-et dhe konferencat e meparshme te organizuara nga Albanian Aligners Academy.")}
        testId="masterclass-testimonials"
      />

      {/* Final CTA */}
      <section className="relative py-20 overflow-hidden">
        <style>{`
          @keyframes gradientShift { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
        `}</style>
        <div className="absolute inset-0" style={{ background: 'linear-gradient(270deg, rgb(0,63,114), rgb(4,63,70), rgb(0,63,114), rgb(4,63,70))', backgroundSize: '300% 300%', animation: 'gradientShift 8s ease infinite' }} />
        <ScrollReveal direction="scale">
          <div className="relative z-10 max-w-[800px] mx-auto px-6 text-center">
          <p className="text-sm uppercase tracking-widest mb-4" style={{ color: 'rgba(255,255,255,0.7)' }}>{tx("Mos e humb kete mundesi")}</p>
          <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ fontFamily: "'Noto Sans', sans-serif", color: '#ffffff' }}>{tx("Siguro Vendin Tend ne Masterclass")}</h2>
          <p className="mb-10 text-lg leading-relaxed" style={{ color: 'rgba(255,255,255,0.8)' }}>{tx("6 module intensive me ekspertet me te mire nderkombetar. Nga teoria ne praktike, nga bazat deri ne Full-Arch Excellence.")}</p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button onClick={() => setShowRegPopup(true)} className="rounded-lg px-10 py-4 font-bold inline-flex items-center justify-center gap-2 bg-white text-[#003F72] hover:shadow-xl hover:-translate-y-0.5 transition-all" data-testid="masterclass-cta-btn">
              {tx("Regjistrohu Tani")} <i className="fas fa-arrow-right text-sm"></i>
            </button>
            <a href="https://wa.me/355696056794?text=Pershendetje%2C%20dua%20te%20di%20me%20shume%20per%20Masterclass-in" target="_blank" rel="noopener noreferrer" className="rounded-lg px-10 py-4 font-bold inline-flex items-center justify-center gap-2 border-2 border-white/30 text-white hover:border-white hover:bg-white/10 transition-all">
              <i className="fab fa-whatsapp text-lg"></i> {tx("Me shume informacion")}
            </a>
          </div>
          </div>
        </ScrollReveal>
      </section>
    </div>
  );
};

export { RegistrationPopup };
export default MasterclassPage;
